import React, { useState, useEffect } from "react";
import { Box, Paper, Typography, TextField, Button, Grid, Tabs, Tab, Stack, Switch, FormControlLabel } from "@mui/material";
import { Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { slaService } from "../../api/services/slaService";
import { PageHeader, Loader } from "../../components/Common";
import DataTable from "../../components/DataTable";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function SlaSettingsPage() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <PageHeader title="SLA Settings" subtitle="Policies, business hours, and holiday calendar" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: "1px solid #eee" }}>
        <Tab label="Policies" />
        <Tab label="Business Hours" />
        <Tab label="Holidays" />
      </Tabs>
      {tab === 0 && <PoliciesTab />}
      {tab === 1 && <BusinessHoursTab />}
      {tab === 2 && <HolidaysTab />}
    </Box>
  );
}

const emptyPolicyForm = { priority_id: "", response_hours: 1, resolution_hours: 4 };
const hoursToMinutes = (hours) => Math.round(Number(hours) * 60);
const minutesToHours = (minutes) => Number((minutes / 60).toFixed(2));
const formatHours = (minutes) => {
  const hours = minutesToHours(minutes);
  return `${hours % 1 === 0 ? hours : hours.toFixed(2)}h`;
};

function PoliciesTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: priorities } = useQuery({ queryKey: ["priorities"], queryFn: slaService.listPriorities, staleTime: Infinity });
  const { data: policies, isLoading } = useQuery({ queryKey: ["sla-policies"], queryFn: slaService.listPolicies });
  const [form, setForm] = useState(emptyPolicyForm);
  const [editingId, setEditingId] = useState(null);

  const escalationMutation = useMutation({
    mutationFn: slaService.runEscalationCheck,
    onSuccess: (res) => enqueueSnackbar(`Escalation check ran — ${res.escalated_count} ticket(s) escalated`, { variant: "info" }),
  });

  const upsertMutation = useMutation({
    mutationFn: () => slaService.upsertPolicy({
      priority_id: Number(form.priority_id),
      response_minutes: hoursToMinutes(form.response_hours),
      resolution_minutes: hoursToMinutes(form.resolution_hours),
    }),
    onSuccess: () => {
      enqueueSnackbar(editingId ? "SLA policy updated" : "SLA policy saved", { variant: "success" });
      setForm(emptyPolicyForm);
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ["sla-policies"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => slaService.deletePolicy(id),
    onSuccess: () => {
      enqueueSnackbar("SLA policy deleted", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["sla-policies"] });
    },
  });

  const priorityName = (id) => priorities?.find((p) => p.id === id)?.name || `#${id}`;

  const startEdit = (policy) => {
    setEditingId(policy.id);
    setForm({
      priority_id: policy.priority_id,
      response_hours: minutesToHours(policy.response_minutes),
      resolution_hours: minutesToHours(policy.resolution_minutes),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyPolicyForm);
  };

  const columns = [
    { key: "priority_id", label: "Priority", render: (r) => priorityName(r.priority_id) },
    { key: "response_minutes", label: "Response SLA", render: (r) => formatHours(r.response_minutes) },
    { key: "resolution_minutes", label: "Resolution SLA", render: (r) => formatHours(r.resolution_minutes) },
    {
      key: "actions", label: "", width: 110,
      render: (r) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => startEdit(r)}>Edit</Button>
          <Button size="small" color="error" onClick={() => deleteMutation.mutate(r.id)} disabled={deleteMutation.isPending}>
            <Trash2 size={16} />
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          {editingId ? "Edit policy" : "Set / update a policy"}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField select label="Priority" size="small" fullWidth SelectProps={{ native: true }}
              disabled={!!editingId}
              value={form.priority_id} onChange={(e) => setForm({ ...form, priority_id: e.target.value })}>
              <option value="" />
              {(priorities || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Response (hours)" size="small" type="number" inputProps={{ step: 0.5, min: 0 }} fullWidth
              value={form.response_hours} onChange={(e) => setForm({ ...form, response_hours: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Resolution (hours)" size="small" type="number" inputProps={{ step: 0.5, min: 0 }} fullWidth
              value={form.resolution_hours} onChange={(e) => setForm({ ...form, resolution_hours: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" fullWidth disabled={!form.priority_id || upsertMutation.isPending} onClick={() => upsertMutation.mutate()}>
                {editingId ? "Update Policy" : "Save Policy"}
              </Button>
              {editingId && <Button onClick={cancelEdit}>Cancel</Button>}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <DataTable columns={columns} rows={policies} loading={isLoading} emptyMessage="No SLA policies configured yet" />

      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" disabled={escalationMutation.isPending} onClick={() => escalationMutation.mutate()}>
          Run Escalation Check Now
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Manual trigger for now — point a cron job at this same backend endpoint for production.
        </Typography>
      </Box>
    </Box>
  );
}

function BusinessHoursTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: existing, isLoading } = useQuery({ queryKey: ["business-hours"], queryFn: slaService.getBusinessHours });

  const [days, setDays] = useState(
    WEEKDAYS.map((_, weekday) => ({ weekday, is_working_day: weekday < 5, start_time: "09:00", end_time: "18:00" }))
  );

  useEffect(() => {
    if (existing && existing.length > 0) {
      setDays(WEEKDAYS.map((_, weekday) => existing.find((d) => d.weekday === weekday) || days[weekday]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: () => slaService.setBusinessHours(days),
    onSuccess: () => {
      enqueueSnackbar("Business hours saved", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["business-hours"] });
    },
  });

  if (isLoading) return <Loader />;

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        {days.map((d, idx) => (
          <Grid container spacing={2} alignItems="center" key={idx}>
            <Grid item xs={3}><Typography variant="body2" sx={{ fontWeight: 600 }}>{WEEKDAYS[idx]}</Typography></Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={<Switch checked={d.is_working_day} onChange={(e) => {
                  const next = [...days]; next[idx] = { ...d, is_working_day: e.target.checked }; setDays(next);
                }} />}
                label="Working day"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField size="small" label="Start" type="time" value={d.start_time} disabled={!d.is_working_day}
                onChange={(e) => { const next = [...days]; next[idx] = { ...d, start_time: e.target.value }; setDays(next); }} />
            </Grid>
            <Grid item xs={3}>
              <TextField size="small" label="End" type="time" value={d.end_time} disabled={!d.is_working_day}
                onChange={(e) => { const next = [...days]; next[idx] = { ...d, end_time: e.target.value }; setDays(next); }} />
            </Grid>
          </Grid>
        ))}
      </Stack>
      <Button variant="contained" sx={{ mt: 3 }} disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
        Save Business Hours
      </Button>
    </Paper>
  );
}

function HolidaysTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: holidays, isLoading } = useQuery({ queryKey: ["holidays"], queryFn: slaService.listHolidays });
  const [form, setForm] = useState({ date: "", name: "" });

  const addMutation = useMutation({
    mutationFn: () => slaService.addHoliday(form),
    onSuccess: () => {
      setForm({ date: "", name: "" });
      enqueueSnackbar("Holiday added", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["holidays"] });
    },
  });
  const removeMutation = useMutation({
    mutationFn: (id) => slaService.removeHoliday(id),
    onSuccess: () => {
      enqueueSnackbar("Holiday removed", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["holidays"] });
    },
  });

  const columns = [
    { key: "date", label: "Date" },
    { key: "name", label: "Name" },
    {
      key: "actions", label: "", width: 60,
      render: (h) => (
        <Button size="small" color="error" onClick={() => removeMutation.mutate(h.id)}><Trash2 size={16} /></Button>
      ),
    },
  ];

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: "flex", gap: 2 }}>
        <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
          value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <TextField size="small" label="Holiday name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ flex: 1 }} />
        <Button variant="contained" disabled={!form.date || !form.name || addMutation.isPending} onClick={() => addMutation.mutate()}>
          Add Holiday
        </Button>
      </Paper>
      <DataTable columns={columns} rows={holidays} loading={isLoading} emptyMessage="No holidays configured" />
    </Box>
  );
}
